import * as stylex from '@stylexjs/stylex';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { ImpactReportForm, ImpactReportHeader } from '@/impactReports/ImpactReportForm.page';
import { availableStages, toImpactReportSelectedItemType } from '@/impactReports/impactReport.types';
import { getStageIndex } from '@/impactReports/impactReport.utilities';
import { useImpactReportCategories } from '@/impactReports/impactReports.store';
import { useImpactReportApiActions } from '@/impactReports/useImpactReportApiActions.hook';
import { hasAnalyticsAdminCapability } from '@/services/authorization.service';
import { SeeqNames } from '@seeqdev/constants';
import { Modal } from '@seeqdev/qomponents';
import type { ImpactReportSelectedItemType } from '@/impactReports/impactReport.types';

interface ImpactReportEditModalProps {
  closeFn: (reloadTable?: boolean) => void;
  existingReport?: Record<string, any>;
}

export const ImpactReportEditModal = ({ closeFn, existingReport }: ImpactReportEditModalProps) => {
  const maxStageIndex = availableStages?.length;
  const maxAllowedStageIndex = hasAnalyticsAdminCapability() ? maxStageIndex - 1 : availableStages.indexOf('quantify');
  const { t } = useTranslation();
  const stateParams = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.stateParams);
  const [workbookId, setWorkbookId] = useState<string>(
    existingReport ? existingReport[SeeqNames.ImpactReport.AssignedToWorkbook] : '',
  );
  const [worksheetId, setWorksheetId] = useState<string>(
    existingReport ? existingReport[SeeqNames.ImpactReport.AssignedToWorksheet] : '',
  );
  const [selectedItemType, setSelectedItemType] = useState<ImpactReportSelectedItemType>(
    toImpactReportSelectedItemType(existingReport?.[SeeqNames.ImpactReport.ImpactBasedOn]),
  );
  const [stageIndex, setStageIndex] = useState(0);
  const categories = useImpactReportCategories();
  const { loadImpactReportCategories } = useImpactReportApiActions();

  useEffect(() => {
    if (existingReport) return;
    setWorkbookId(stateParams?.workbookId);
    setWorksheetId(stateParams?.worksheetId);
  }, [stateParams]);

  useAsyncEffect(async () => {
    if (!categories) {
      await loadImpactReportCategories();
    }
  }, []);

  useEffect(() => {
    if (!existingReport) return;
    const idx = getStageIndex(existingReport[SeeqNames.ImpactReport.ImpactStatus]);
    const nextIdx = idx < maxStageIndex - 1 ? idx + 1 : idx;
    setStageIndex(Math.min(nextIdx, maxAllowedStageIndex));
  }, [existingReport, maxAllowedStageIndex, maxStageIndex]);

  return categories ? (
    <Modal
      testId="impactReportEditModal"
      hideFooterButtons={true}
      open={true}
      size="4xl"
      onClose={closeFn}
      title={t('IMPACT_REPORT.CAPTURE_IMPACT')}
      customHeader={
        <ImpactReportHeader
          workbookId={workbookId}
          worksheetId={worksheetId}
          selectedItemType={selectedItemType}
          stageIndex={stageIndex}
          setStageIndex={setStageIndex}
          setWorkbookId={setWorkbookId}
          setWorksheetId={setWorksheetId}
          setSelectedItemType={setSelectedItemType}
        />
      }>
      <div className={`${stylex.props(styles.content).className} valueCapture`}>
        <ImpactReportForm
          categories={categories}
          report={existingReport}
          stageIndex={stageIndex}
          workbookId={workbookId}
          worksheetId={worksheetId}
          selectedItemType={selectedItemType}
          setWorkbookId={setWorkbookId}
          setWorksheetId={setWorksheetId}
          setSelectedItemType={setSelectedItemType}
          setStageIndex={setStageIndex}
          closeFn={closeFn}
        />
      </div>
    </Modal>
  ) : null;
};

const styles = stylex.create({
  content: { maxHeight: 800, height: '90%' },
});
